import type { ClientsCloseOverviewSlimQuery } from "@common/schemas/clientsCloseOverview.schema";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import { getCellKey } from "@design-system/Board/boardCells";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import type { WorkBoardColumnBy } from "./workBoardColumns";
import type { WorkBoardLaneBy } from "./workBoardLanes";

/**
 * What the board's cards are fetched and grouped for: the page's query
 * (filters, search, sort, page) and the grouping. A change here moves cards
 * for a reason other than a live update.
 */
export interface WorkBoardScope {
    listQuery: ClientsCloseOverviewSlimQuery;
    columnBy: WorkBoardColumnBy;
    laneBy: WorkBoardLaneBy | null;
}

/** The cells each card sits in after one data refresh. */
export interface WorkBoardSnapshot {
    scopeKey: string;
    cellsKeyByRowId: ReadonlyMap<string, string>;
    placementsByRowId: ReadonlyMap<string, readonly BoardPlacement[]>;
}

export const NO_IDS: readonly string[] = [];

export const getWorkBoardSnapshot = ({
    scope,
    rows,
    getPlacements,
}: {
    scope: WorkBoardScope;
    rows: readonly ClientsWorkOverviewWorkRow[];
    getPlacements: (row: ClientsWorkOverviewWorkRow) => BoardPlacement[];
}): WorkBoardSnapshot => {
    const placementsByRowId = new Map(
        rows.map((row) => [row.id, getPlacements(row)]),
    );
    return {
        scopeKey: JSON.stringify(scope),
        placementsByRowId,
        cellsKeyByRowId: new Map(
            [...placementsByRowId].map(([rowId, placements]) => [
                rowId,
                placements.map(getCellKey).join("|"),
            ]),
        ),
    };
};

/** Whether two snapshots put the same cards in the same cells for one scope. */
export const isSameWorkBoardSnapshot = (
    previous: WorkBoardSnapshot | null,
    next: WorkBoardSnapshot | null,
): boolean => {
    if (previous === null || next === null) {
        return previous === next;
    }
    return (
        previous.scopeKey === next.scopeKey &&
        previous.cellsKeyByRowId.size === next.cellsKeyByRowId.size &&
        [...next.cellsKeyByRowId].every(
            ([rowId, cellsKey]) =>
                previous.cellsKeyByRowId.get(rowId) === cellsKey,
        )
    );
};

/**
 * Cards a live refresh moved to another column or lane: on both snapshots,
 * in other cells, with the scope unchanged. The first load and a scope
 * change move nothing. Cards a refresh adds don't count either: a card also
 * shows up when a details refetch or a delete on the page reshuffles the
 * rows, so an added card isn't reliably a new application.
 */
export const getLiveMovedCardIds = (
    previous: WorkBoardSnapshot | null,
    next: WorkBoardSnapshot,
): readonly string[] => {
    if (previous === null || previous.scopeKey !== next.scopeKey) {
        return NO_IDS;
    }
    const movedIds = [...next.cellsKeyByRowId]
        .filter(([rowId, cellsKey]) => {
            const previousCellsKey = previous.cellsKeyByRowId.get(rowId);
            return (
                previousCellsKey !== undefined && previousCellsKey !== cellsKey
            );
        })
        .map(([rowId]) => rowId);
    return movedIds.length === 0 ? NO_IDS : movedIds;
};
